'use client';

import { Box, type SxProps } from '@mui/material';
import Handlebars from 'handlebars';
import { type ReactNode, useMemo } from 'react';

import type { RawProductApiResponse } from '@campaign-manager/core/model';
import { Text } from '@campaign-manager/react/components';

// Register the global with_placeholder helper (doesn't need instance data)
Handlebars.registerHelper('with_placeholder', (value, placeholder) => {
  if (!value || value === '' || value === null || value === undefined) {
    return new Handlebars.SafeString(placeholder);
  }

  return value;
});

// Comparison helpers for conditional rendering
Handlebars.registerHelper('eq', (a, b) => {
  return a === b;
});

Handlebars.registerHelper('ne', (a, b) => {
  return a !== b;
});

Handlebars.registerHelper('contains', (haystack, needle) => {
  if (typeof haystack === 'string') return haystack.includes(needle);
  if (Array.isArray(haystack)) return haystack.includes(needle);
  return false;
});

interface ProductTemplateData {
  name?: string;
  description?: string;
  brand?: string;
  image_url?: string;
  categories?: Array<{
    category_key: string;
    category_name: string;
  }>;
}

interface TemplateData {
  custom_parameters?: Record<string, any>;
  /** Custom targeting by variable key; a line item stores boolean expressions, an ad set values. */
  custom_targeting?: Record<string, any>;
  advertising?: {
    headline?: string;
    link_url?: string;
    creatives?: Record<string, { url: string }>;
  };
  products?: RawProductApiResponse[] | ProductTemplateData[] | null;
}

interface Props {
  strict?: boolean;
  htmlTemplate: string;
  data: TemplateData;
  sx?: SxProps;
  containerSx?: SxProps;
  errorFallback?: React.ReactNode;
  title?: string;
}

export default function HandlebarsTemplateRenderer({
  strict = false,
  htmlTemplate,
  data,
  sx,
  containerSx,
  errorFallback,
  title = 'Template Preview',
}: Props) {
  const { renderedContent, error } = useMemo(() => {
    if (!htmlTemplate.trim()) {
      return { renderedContent: null, error: undefined };
    }

    try {
      const compiledTemplate = Handlebars.compile(htmlTemplate, { strict });
      return { renderedContent: compiledTemplate(data), error: undefined };
    } catch (error) {
      return { renderedContent: null, error };
    }
  }, [strict, htmlTemplate, JSON.stringify(data)]);

  if (!htmlTemplate.trim()) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', ...containerSx }}>
        <Text
          size="sm"
          color="grey"
        >
          No template content to preview
        </Text>
      </Box>
    );
  }

  if (!error && !renderedContent?.trim()) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', ...containerSx }}>
        <Text
          size="sm"
          color="grey"
        >
          The template rendered no content for this data
        </Text>
      </Box>
    );
  }

  if (!renderedContent || error) {
    return (
      <ErrorMessage
        error={error}
        errorFallback={errorFallback}
        sx={sx}
      />
    );
  }

  return (
    <Box
      sx={{
        overflow: 'hidden',
        ...containerSx,
      }}
    >
      <iframe
        srcDoc={renderedContent}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          display: 'block',
        }}
        title={title}
        sandbox="allow-scripts"
      />
    </Box>
  );
}

interface ErrorMessageProps {
  error: any;
  errorFallback?: ReactNode;
  sx?: SxProps;
}

function ErrorMessage({ error, errorFallback, sx }: ErrorMessageProps) {
  if (errorFallback) {
    return <Box sx={sx}>{errorFallback}</Box>;
  }

  const detail =
    error instanceof Error ? error.message : typeof error === 'string' ? error : undefined;

  return (
    <Box sx={{ p: 2, backgroundColor: 'rgba(255, 0, 0, 0.05)', borderRadius: 1, ...sx }}>
      <Text
        size="sm"
        color="error"
      >
        Your template has a syntax error:
      </Text>
      {detail && (
        <Box
          component="pre"
          sx={{
            mt: 1,
            mb: 0,
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: 'error.main',
          }}
        >
          {detail}
        </Box>
      )}
    </Box>
  );
}

export type { TemplateData };
